import { Box, ToggleButtonGroup, ToggleButton, TextField, Card, Button, Chip } from "@mui/material";
import { FC, useState } from "react";
import ClarnetController from "../../../../../Api/clarnetController";
import { v4 as uuidv4 } from 'uuid';
import { ICategory } from "../../../../../interfaces/ICategory";
import { categoryData } from "../../../../Furniture/categoryData";

interface SelectAddModeProps {
    mode: 'manual' | 'scrap';
    setMode: (mode: 'manual' | 'scrap') => void;
    onSetFurniture: any;
    commonValues: any;
    furnitureLength: number;
}


const normalize = (s: string) =>
    s.toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

// Bez zbędnych struktur pośrednich – rekursja po categoryData
const detectCategories = (name: string): string[] => {
    const n = normalize(name);
    const result: string[] = [];

    const walk = (cats: ICategory[]) => {
        for (const cat of cats) {
            if (cat.triggers?.some(t => n.includes(normalize(t)))) {
                if (!result.includes(cat.name)) result.push(cat.name);
            }
            if (cat.subcategories?.length) walk(cat.subcategories);
        }
    };

    walk(categoryData);
    return result;
};


const SelectAddMode: FC<SelectAddModeProps> = ({
    mode,
    setMode,
    onSetFurniture,
    commonValues,
    furnitureLength,
}) => {
    const [url, setUrl] = useState<string>('');
    const [loading, setLoading] = useState(false);

    const handleScrapPage = async () => {
        if (!url) return;
        setLoading(true);
        try {
            const res = await ClarnetController.scrapeCollection(url);

            if (res.ok && res.products) {
                const newFurniture = res.products.map((product, index) => {
                    const toNum = (v?: string) => {
                        if (!v) return 0;
                        const n = parseFloat(v.replace(',', '.'));
                        return isNaN(n) ? 0 : n;
                    };
                    const featuresText = (product.features || [])
                        .map((f: string) => `· ${f}`)
                        .join('\n');
                    let combinedDescription = '';
                    const desc = product.description?.trim();
                    const feats = featuresText.trim();
                    if (desc && feats) {
                        combinedDescription = `${desc}\n\n${feats}`;
                    } else if (desc) {
                        combinedDescription = desc;
                    } else {
                        combinedDescription = feats;
                    }

                    const rawName = `${product.productTitle.toUpperCase()} ${product.productIndex.toUpperCase()}`.trim();
                    const autoCategories = detectCategories(rawName);

                    return ({
                        id: index + 1,
                        name: `${product.productTitle.toUpperCase()} ${product.productIndex.toUpperCase()}` || '',
                        producer: commonValues.producerId || '', // lub product.producer jeśli masz
                        partCollection: commonValues.collectionId || '', // lub product.partCollectionId jeśli masz
                        price: 0, // lub product.price jeśli masz
                        width: toNum(product.width),
                        height: toNum(product.height),
                        depth: toNum(product.depth),
                        crossed: 0, // lub product.crossed jeśli masz
                        isPriceVissible: false, // lub product.isPriceVissible jeśli masz
                        description: combinedDescription,
                        images: (product.images || []).map((img, i) => {
                            const byteString = window.atob(img.base64);
                            const array = [];
                            for (let j = 0; j < byteString.length; j++) {
                                array.push(byteString.charCodeAt(j));
                            }
                            const mime = img.mime || "image/jpeg";
                            const extension = mime.split('/')[1] || 'jpg';
                            const blob = new Blob([new Uint8Array(array)], { type: mime });
                            return new File([blob], `image_${index}_${i}.${extension}`, { type: mime });
                        }),
                        premises: commonValues.premises || [],
                        furnitureCategories: autoCategories,
                        randomKey: uuidv4(),
                    });
                });

                onSetFurniture((prevState: any) => [...prevState, ...newFurniture]);
            }
        } catch (err) {
            // Możesz dodać obsługę błędu, np. toast/error
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {mode === 'scrap' &&
                <Card
                    sx={{
                        m: 2,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        alignItems: 'center',
                        boxShadow: 3,
                        borderRadius: 3,
                        background: '#fafafa',
                    }}
                >
                    <TextField
                        label="Wklej link do kolekcji"
                        variant="filled"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        sx={{
                            width: 320,
                            background: '#fff',
                            borderRadius: 2,
                            '& .MuiFilledInput-root': { borderRadius: 2 },
                        }}
                    />
                    <Button
                        onClick={handleScrapPage}
                        variant="contained"
                        disabled={loading || !url}
                        sx={{
                            width: 180,
                            fontWeight: 700,
                            background: '#c85742',
                            color: '#fff',
                            borderRadius: 2,
                            boxShadow: 1,
                            '&:hover': { background: '#a9442e' },
                        }}
                    >

                        {loading ? "Ładowanie..." : "Pobierz"}
                    </Button>
                </Card>
            }
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'center' }}>
                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={(_, value) => value && setMode(value)}
                    sx={{
                        borderRadius: 2,
                        boxShadow: 2,
                        minWidth: 320,
                        background: '#fff',
                        overflow: 'hidden',
                    }}
                >
                    <ToggleButton
                        value="manual"
                        sx={{
                            width: 160,
                            py: 1.5,
                            fontWeight: 700,
                            color: mode === 'manual' ? '#fff' : '#232323',
                            background: mode === 'manual' ? '#c85742' : 'transparent',
                            '&:hover': {
                                background: mode === 'manual' ? '#c85742' : '#f5e6e1',
                            },
                            border: 'none',
                            transition: 'background 0.2s, color 0.2s',
                        }}
                    >
                        RĘCZNE
                    </ToggleButton>
                    <ToggleButton
                        value="scrap"
                        sx={{
                            width: 160,
                            py: 1.5,
                            fontWeight: 700,
                            color: mode === 'scrap' ? '#fff' : '#232323',
                            background: mode === 'scrap' ? '#c85742' : 'transparent',
                            '&:hover': {
                                background: mode === 'scrap' ? '#c85742' : '#f5e6e1',
                            },
                            border: 'none',
                            transition: 'background 0.2s, color 0.2s',
                        }}
                    >
                        SCRAPOWANIE
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <Chip
                label={furnitureLength}
                color="primary"
                sx={{ mb: 2 }}
            />
        </>
    )
}

export default SelectAddMode;